import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { UtilityService } from './utility.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

export const FlowStorageKeys = {
    packagesFlowReturnTo: 'packagesFlowReturnTo',
    searchFlowReturnTo: 'searchFlowReturnTo',
} as const;

type FlowStorageKey = (typeof FlowStorageKeys)[keyof typeof FlowStorageKeys];

type Tabs1Based = {
    totalTabs: () => number;
    currentTab: () => number;
    goToTab: (step1: number) => void;
};

@Injectable({ providedIn: 'root' })
export class FlowRouteService {
    private fullscreenRestore?: () => void;
    private isFullscreenApplied: boolean = false;

    constructor(
        private router: Router,
        private utilityService: UtilityService,
        private translateService: TranslateService,
    ) {
        if (!this.utilityService.isBrowser()) {
            return;
        }
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => this.syncFullscreenLayoutToRoute());
        queueMicrotask(() => this.syncFullscreenLayoutToRoute());
    }

    setReturnTo(storageKey: string, url: string): void {
        if (!this.utilityService.isBrowser()) return;
        if (!storageKey) return;
        localStorage.setItem(storageKey, url ?? '');
    }

    private readonly routeKeyByStorageKey: Record<FlowStorageKey, string> = {
        [FlowStorageKeys.packagesFlowReturnTo]: 'Misc_packages',
        [FlowStorageKeys.searchFlowReturnTo]: 'Misc_search_dialog',
    };

    private readonly defaultQueryParamsByStorageKey: Partial<Record<FlowStorageKey, Record<string, any>>> = {
        [FlowStorageKeys.packagesFlowReturnTo]: { step: 1 },
        [FlowStorageKeys.searchFlowReturnTo]: { step: 1 },
    };

    async navigateToFlow(
        storageKey: FlowStorageKey,
        opts?: { returnToUrl?: string; queryParams?: Record<string, any> },
    ): Promise<boolean> {
        const returnToUrl = opts?.returnToUrl ?? this.router.url;
        this.setReturnTo(storageKey, returnToUrl);
        const defaults = this.defaultQueryParamsByStorageKey[storageKey];
        const queryParams = defaults ? { ...defaults, ...opts?.queryParams } : opts?.queryParams;
        return this.navigateByRouteKey(this.routeKeyByStorageKey[storageKey], queryParams);
    }

    initReturnTo(storageKey: string): string | undefined {
        if (!this.utilityService.isBrowser()) return undefined;
        if (!storageKey) return undefined;
        const v = localStorage.getItem(storageKey);
        return v || undefined;
    }

    clearReturnTo(storageKey: string): void {
        if (!this.utilityService.isBrowser()) return;
        if (!storageKey) return;
        localStorage.removeItem(storageKey);
    }

    setJson(key: string, value: unknown): void {
        if (!this.utilityService.isBrowser()) return;
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            if (!environment.production) console.error(error);
        }
    }

    getJson<T>(key: string): T | undefined {
        if (!this.utilityService.isBrowser()) return undefined;
        if (!key) return undefined;
        const raw = localStorage.getItem(key);
        if (!raw) return undefined;
        try {
            return JSON.parse(raw) as T;
        } catch (error) {
            if (!environment.production) console.error(error);
        }
        return undefined;
    }

    clearKey(key: string): void {
        if (!this.utilityService.isBrowser()) return;
        if (!key) return;
        localStorage.removeItem(key);
    }

    syncStepParam1Based(args: {
        route: ActivatedRoute;
        router: Router;
        paramKey: string;
        defaultStep: number;
        maxStep: () => number;
        minStep?: () => number;
        getStep: () => number;
        setStep: (step: number) => void;
    }): { sub: Subscription; writeStep: (step: number, replaceUrl?: boolean) => void } {
        const sanitizeStep = (step: number) => {
            const maxStep = Number.isFinite(args.maxStep?.()) ? args.maxStep() : 1;
            const minStep = args.minStep?.() ?? 1;
            if (!Number.isFinite(step)) {
                return Math.max(minStep, Math.min(maxStep, args.defaultStep ?? 1));
            }
            return Math.max(minStep, Math.min(maxStep, step));
        };

        const writeStep = (step: number, replaceUrl: boolean = false) => {
            const stepInUrl = sanitizeStep(step);
            args.router.navigate([], {
                relativeTo: args.route,
                queryParams: { [args.paramKey]: stepInUrl },
                queryParamsHandling: 'merge',
                replaceUrl,
            });
        };

        const readStepFromUrl = (): { stepFromUrl: number; shouldRewriteUrl: boolean } => {
            const stepParamRaw = args.route.snapshot.queryParamMap.get(args.paramKey);
            if (stepParamRaw === null || stepParamRaw === undefined || stepParamRaw === '') {
                return { stepFromUrl: sanitizeStep(args.defaultStep ?? 1), shouldRewriteUrl: false };
            }
            const parsedStep = Number(stepParamRaw);
            const stepFromUrl = sanitizeStep(parsedStep);
            const shouldRewriteUrl = !Number.isFinite(parsedStep) || String(stepFromUrl) !== stepParamRaw;
            return { stepFromUrl, shouldRewriteUrl };
        };

        const sub = args.route.queryParamMap.subscribe(() => {
            const { stepFromUrl, shouldRewriteUrl } = readStepFromUrl();
            const currentStep = sanitizeStep(args.getStep?.() ?? 1);
            if (stepFromUrl !== currentStep) {
                args.setStep(stepFromUrl);
            }
            if (shouldRewriteUrl) {
                writeStep(stepFromUrl, true);
            }
        });
        return { sub, writeStep };
    }

    syncTabsWithStepParam1Based(args: {
        route: ActivatedRoute;
        router: Router;
        paramKey: string;
        tabs: Tabs1Based;
        defaultStep?: number;
        minStep?: () => number;
        onBeforeStepApplied?: () => void;
        onAfterStepApplied?: () => void;
    }): { sub: Subscription; writeStep: (step: number, replaceUrl?: boolean) => void } {
        const defaultStep = args.defaultStep ?? 1;
        return this.syncStepParam1Based({
            route: args.route,
            router: args.router,
            paramKey: args.paramKey,
            defaultStep,
            maxStep: () => Math.max(1, args.tabs.totalTabs()),
            minStep: args.minStep,
            getStep: () => Math.max(1, args.tabs.currentTab()),
            setStep: (s1) => {
                args.onBeforeStepApplied?.();
                args.tabs.goToTab(s1);
                args.onAfterStepApplied?.();
            },
        });
    }

    replaceMergedQueryParam(
        route: ActivatedRoute,
        key: string,
        value: any,
    ): Promise<boolean> {
        return this.router.navigate([], {
            relativeTo: route,
            queryParams: { [key]: value },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    async forceStepToFirst(args: {
        route: ActivatedRoute;
        router: Router;
        paramKey: string;
        firstStep?: number;
    }): Promise<boolean> {
        const raw = this.getQueryParamValue(args.route, args.paramKey);
        const first = args.firstStep ?? 1;
        if (!raw || Number(raw) === first) {
            return false;
        }
        await this.replaceMergedQueryParam(args.route, args.paramKey, first);
        return true;
    }

    async navigateByRouteKey(routeKey: string, queryParams?: Record<string, any>): Promise<boolean> {
        const route = await firstValueFrom(this.translateService.get(`ROUTES.${routeKey}`));
        return this.router.navigate([route], queryParams ? { queryParams } : undefined);
    }

    private getQueryParamValue(route: ActivatedRoute, key: string): string | undefined {
        const raw = route.snapshot.queryParamMap.get(key);
        if (raw === null || raw === undefined || raw === '') return undefined;
        return raw;
    }

    private syncFullscreenLayoutToRoute(): void {
        const isFlow = this.isFullscreenFlowRouteActive();
        if (isFlow === this.isFullscreenApplied) {
            return;
        }
        if (isFlow) {
            this.fullscreenRestore = this.applyFlowLayout();
            this.isFullscreenApplied = true;
            return;
        }
        this.fullscreenRestore?.();
        this.fullscreenRestore = undefined;
        this.isFullscreenApplied = false;
    }

    private isFullscreenFlowRouteActive(): boolean {
        let snap = this.router?.routerState?.snapshot?.root;
        if (!snap) return false;
        while (snap.firstChild) {
            snap = snap.firstChild;
        }
        return !!snap.data?.['fullscreenFlow'];
    }

    private applyFlowLayout(): () => void {
        const snapshot = {
            isSemiFullScreen: this.utilityService.isSemiFullScreen,
            isFooterVisible: this.utilityService.isFooterVisible,
            isHeaderHidden: this.utilityService.isHeaderHidden,
        };

        this.utilityService.isSemiFullScreen = true;
        this.utilityService.isFooterVisible = false;
        this.utilityService.isHeaderHidden = true;

        return () => {
            this.utilityService.isSemiFullScreen = snapshot.isSemiFullScreen;
            this.utilityService.isFooterVisible = snapshot.isFooterVisible;
            this.utilityService.isHeaderHidden = snapshot.isHeaderHidden;
        };
    }
}

